/**
 * Shared config for the setup flow (setupPage and performanceOnboardingBanner).
 * Step titles and layout styles so both flows stay in sync.
 */

// Layout / padding
const PADDING_DEFAULT = "24px";
const PADDING_LARGE = "48px";
const PADDING_LARGE_HORIZONTAL = "32px";

// Card dimensions
const CARD_MAX_WIDTH_DEFAULT = "800px";
const CARD_MAX_WIDTH_NARROW = "700px";
const CARD_MAX_WIDTH_WIDE = "900px";
const CARD_MAX_WIDTH_WIDEST = "980px";
const CARD_MAX_HEIGHT_DEFAULT = "64vh";
const CARD_MAX_HEIGHT_NONE = "none";
const CARD_MIN_HEIGHT_DEFAULT = "400px";
const CARD_MIN_HEIGHT_NONE = "0px";

// Card appearance
const CARD_BORDER_RADIUS = "16px";
const CARD_BORDER = "1px solid #21212114";
const CARD_BOX_SHADOW = "0px 2px 4px 0px #00000013";

export const stepTitles = {
  0: ["Business Information", "Business Mode", "Review & Confirm"],
  1: ["Bank Link Info", "Account Verification", "Finalize Setup"],
  2: [
    "Team Overview",
    "Capacity Overview",
    "Performance Drivers",
    "Review & Confirm",
  ],
};

const STEP_HEADER_LABELS = [
  "Set Up",
  "Create & Link Bank Accounts",
  "Performance Overview",
  "Schedule Intro Call",
];

export const getStepHeaderLabel = (stepperStep) => {
  return STEP_HEADER_LABELS[stepperStep] ?? "";
};

/**
 * Card sx for the main setup flow card. Depends on stepperStep and part2Step.
 */
const getSetupFlowLayoutVariant = (stepperStep, part2Step) => {
  const isScheduleCallStep = stepperStep === 3;
  const isSuccessStep = stepperStep === 4;
  const isBankSetupIntro = stepperStep === 1 && part2Step === 0;
  const isBankSetupCompletion = stepperStep === 1 && part2Step === 3;

  return {
    isScheduleCallStep,
    isSuccessStep,
    isBankSetupIntro,
    isBankSetupCompletion,
  };
};

export const getSetupFlowCardSx = (stepperStep, part2Step) => {
  const {
    isScheduleCallStep,
    isSuccessStep,
    isBankSetupIntro,
    isBankSetupCompletion,
  } = getSetupFlowLayoutVariant(stepperStep, part2Step);

  let maxWidth = CARD_MAX_WIDTH_DEFAULT;
  if (isSuccessStep) maxWidth = CARD_MAX_WIDTH_NARROW;
  if (isScheduleCallStep || isBankSetupIntro) maxWidth = CARD_MAX_WIDTH_WIDE;
  if (isBankSetupCompletion) maxWidth = CARD_MAX_WIDTH_WIDEST;

  const maxHeight = isBankSetupCompletion
    ? CARD_MAX_HEIGHT_NONE
    : CARD_MAX_HEIGHT_DEFAULT;

  return {
    width: {
      xs: "calc(100% - 50px)",
      sm: "calc(100% - 50px)",
      md: "100%",
    },
    height: isBankSetupCompletion ? "auto" : "100%",
    maxWidth,
    maxHeight,
    minHeight: isBankSetupCompletion ? CARD_MIN_HEIGHT_NONE : CARD_MIN_HEIGHT_DEFAULT,
    borderRadius: CARD_BORDER_RADIUS,
    border: CARD_BORDER,
    boxShadow: CARD_BOX_SHADOW,
    display: "flex",
    flexDirection: "column",
  };
};

/**
 * CardContent sx for the setup flow. Depends on stepperStep and part2Step.
 */
export const getSetupFlowCardContentSx = (stepperStep, part2Step) => {
  const {
    isScheduleCallStep,
    isSuccessStep,
    isBankSetupIntro,
    isBankSetupCompletion,
  } = getSetupFlowLayoutVariant(stepperStep, part2Step);

  const isZeroPadding = isScheduleCallStep || isBankSetupCompletion;

  let pt = PADDING_DEFAULT;
  let px = PADDING_DEFAULT;
  let pb = PADDING_DEFAULT;

  if (isZeroPadding) {
    pt = 0;
    px = 0;
    pb = 0;
  } else if (isSuccessStep) {
    pt = PADDING_LARGE;
    px = PADDING_LARGE;
    pb = PADDING_LARGE;
  } else if (isBankSetupIntro) {
    pt = PADDING_LARGE;
    px = PADDING_LARGE_HORIZONTAL;
    pb = PADDING_LARGE;
  }

  return {
    flex: 1,
    pt,
    px,
    pb: `${pb} !important`,
    overflowY: "auto",
  };
};
